/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Typography, CircularProgress } from "@material-ui/core";
import { SystemEntity } from "@backstage/catalog-model";
import { FeaturesListingCard } from "./components/FeaturesListingCard";
import { FeaturesManagementCard } from "./components/FeaturesManagementCard";
import { Alert, Color, Skeleton } from "@material-ui/lab";
import { discoveryApiRef, fetchApiRef, useApi } from "@backstage/core-plugin-api";
import { FeatureFlag } from "./types";
import { FeatureFlagService } from "../../../../services/FeatureFlagService";
import { scaffolderApiRef } from "@backstage/plugin-scaffolder-react";
import React from "react";

export const FeaturesChangeSubpage = ({ system }: { system: SystemEntity }) => {

  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const featureFlagService = new FeatureFlagService(fetchApi, discoveryApi);
  const scaffolderApi = useApi(scaffolderApiRef);

  const [initialFeatureFlags, setInitialFeatureFlags] = useState<FeatureFlag[]>([]);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [featureFlagsChanged, setFeatureFlagsChanged] = useState<boolean>(false);
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [modalResetConfirmationOpen, setModalResetConfirmationOpen] = useState(false);
  const [modalSubmitConfirmationOpen, setModalSubmitConfirmationOpen] = useState(false);
  const [submitInProcessing, setSubmitInProcessing] = useState(false);

  const [openOperationAlert, setOpenOperationAlert] = useState(false);
  const [operationMsg, setOperationMsg] = useState('');
  const [operationSeverity, setOperationSeverity] = useState<Color>('success');

  const searchFeatureFlags = () => {
    featureFlagService.searchFeatureFlagsBySystem(system)
      .then(res => {
        setFeatureFlags(res.items);
        setInitialFeatureFlags(res.items);
      });
  }

  useEffect(() => {
    searchFeatureFlags();
  }, []);

  useEffect(() => {
    if (featureFlags.some(flag => flag.wasUpdated)) {
      setFeatureFlagsChanged(true);
    }
  }, [featureFlags]);


  const getFeatureFlagDetails = async (flagId: string) => {
    setLoadingData(true);
    const searchedFeatureFlag = featureFlags.find(flag => flag.id === flagId && flag.wasSearched);

    if (searchedFeatureFlag) {
      setSelectedFlag(searchedFeatureFlag);
    } else {
      featureFlagService.getFeatureFlagDetails(flagId)
        .then(flagData => {
          flagData.wasSearched = true;
          setSelectedFlag(flagData);
          setFeatureFlags(prevFlags => {
            return prevFlags.map(flag => {
              if (flag.id === flagId) {
                return { ...flag, ...flagData };
              }
              return flag;
            });
          });
        });
    }
    setLoadingData(false);
  };

  const showSubmitToEditAlert = (feat: FeatureFlag) => {
    setOperationMsg(`You can only edit '${feat.code}' after it was submitted.`);
    setOperationSeverity('error');
    setOpenOperationAlert(true);
  }

  const selectFlag = (flag: FeatureFlag) => {
    if (flag.wasCreated) {
      showSubmitToEditAlert(flag);
      return;// Prevent selecting a flag that is being created
    }
    setSelectedFlag(null);

    if (selectedFlag?.code !== flag.code) {
      setSelectedFlag({ ...flag });
      if (flag.id)
        getFeatureFlagDetails(flag.id);
    }
  }

  const onUpdate = (updatedFlag: FeatureFlag) => {
    updatedFlag.wasUpdated = true;
    setSelectedFlag(updatedFlag);
    const updatedFeatureFlags = featureFlags.map(flag => {
      if (flag.code === updatedFlag.code) {
        return updatedFlag;
      }
      return flag;
    });
    setFeatureFlags(updatedFeatureFlags);
  };

  const showProcessingAlert = () => {
    setOperationMsg('The changes are being submitted, please wait while we process the request.');
    setOperationSeverity('info');
    setOpenOperationAlert(true);
    setSubmitInProcessing(true);
  }

  const removeTransientStateProperty = (flag: FeatureFlag) => {
    const { wasSearched, wasUpdated, wasCreated, ...cleanFlag } = flag;
    return cleanFlag;
  }

  const handleSubmitChanges = () => {
    setModalSubmitConfirmationOpen(false);
    showProcessingAlert();
    const updatedFeatureFlags = featureFlags.filter(flag => flag.wasUpdated).map(removeTransientStateProperty);
    const prBranchName = `feat/backstage/${system.metadata.name}-${Math.floor(Math.random() * 100000)}`;

    scaffolderApi.scaffold({
      templateRef: 'feature-flag-management-template',
      values: {
        system: `${system.metadata.namespace}-${system.metadata.name}`,
        namespace: `${system.metadata.namespace}`,
        featureFlags: updatedFeatureFlags as any[],
        prBranchName,
        projectDeployRepo: `https://dev.azure.com/totvstfs/SupplyEngenharia/_git/open-feature-flags`
      }
    })
      .then(async response => {
        let taskCompleted = false;
        while (!taskCompleted) {
          const task = await scaffolderApi.getTask(response.taskId);
          if (task.status === 'completed') {
            taskCompleted = true;
            setFeatureFlagsChanged(false);
          } else if (task.status === 'failed') {
            setOperationMsg('Scaffolder task failed!');
            setOperationSeverity('error');
            setOpenOperationAlert(true);
            setSubmitInProcessing(false);
            return;
          } else if (task.status === 'cancelled') {
            setOperationMsg('Scaffolder task was cancelled!');
            setOperationSeverity('error');
            setOpenOperationAlert(true);
            setSubmitInProcessing(false);
            return;
          } else {
            showProcessingAlert();
            await new Promise(res => setTimeout(res, 2000));
          }
        }

        featureFlagService.updateFeatureFlags(updatedFeatureFlags)
          .then(res => {
            if (res) {
              searchFeatureFlags();
              setOperationMsg('Feature flags updated successfully!');
              setOperationSeverity('success');
            } else {
              setOperationMsg('Error updating feature flags!');
              setOperationSeverity('error');
            }
            setOpenOperationAlert(true);
            setSubmitInProcessing(false);
          });
      })
      .catch(() => {
        setOperationMsg('Error starting scaffolder task!');
        setOperationSeverity('error');
        setOpenOperationAlert(true);
        setSubmitInProcessing(false);
      });
  };

  const resetChanges = () => {
    setFeatureFlags(initialFeatureFlags);
    setSelectedFlag(null);
    setModalResetConfirmationOpen(false);
    setFeatureFlagsChanged(false);
  };

  return (
    <>
      <Card style={{ marginTop: 16 }}>
        <CardContent>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                Feature management
              </Typography>
              <Typography variant="body1" gutterBottom>
                The changes made here only will be applied when the changes are
                submited
              </Typography>
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="end"
              gridColumnGap={16}
            >
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                disabled={!featureFlagsChanged || submitInProcessing}
                onClick={() => setModalResetConfirmationOpen(true)}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                disabled={!featureFlagsChanged || submitInProcessing}
                onClick={() => setModalSubmitConfirmationOpen(true)}
              >
                {submitInProcessing ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Submit'
                )}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box
        mt={2}
        display="flex"
        height={550}
        flexDirection="row"
        gridColumnGap={16}
      >
        <Card style={{ flexShrink: 0 }}>
          <CardContent>
            <FeaturesListingCard
              system={system}
              selectedFlag={selectedFlag}
              setSelectedFlag={selectFlag}
              featureFlags={featureFlags}
              setFeatureFlags={setFeatureFlags}
            />
          </CardContent>
        </Card>

        {selectedFlag && !loadingData && (
          <FeaturesManagementCard
            selectedFlag={selectedFlag}
            onUpdate={onUpdate}
          />
        )}

        {loadingData && (
          <Card style={{ flexGrow: 1 }}>
            <CardContent>
              <Box
                display="flex"
                flexDirection="column"
                gridRowGap={24}
                height="100%"
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={4}
                >
                  <Skeleton variant="text" width={100} height={30} />
                  <Skeleton variant="text" width={100} height={30} />
                </Box>
                <Skeleton variant="rect" width="100%" height={55} />
                <Box display="flex" gridColumnGap={8}>
                  <Skeleton variant="rect" width={130} height={35} />
                  <Skeleton variant="rect" width={130} height={35} />
                  <Skeleton variant="rect" width={130} height={35} />
                </Box>
                <Box display="flex" gridColumnGap={8}>
                  <Skeleton variant="rect" width={150} height={35} />
                  <Skeleton variant="rect" width={250} height={35} />
                </Box>
                <Skeleton variant="rect" width="100%" height={250} />
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {modalResetConfirmationOpen && (
        <Dialog
          open={modalResetConfirmationOpen}
          maxWidth="lg"
          onClose={() => setModalResetConfirmationOpen(false)}
        >
          <DialogTitle>Reset Changes</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to reset all changes? <br />
              This action will <strong>discard all modifications</strong> and
              revert to the initial state of the feature flags.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setModalResetConfirmationOpen(false)}
              color="primary"
            >
              Cancel
            </Button>
            <Button onClick={() => resetChanges()} color="secondary">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {modalSubmitConfirmationOpen && (
        <Dialog
          open={modalSubmitConfirmationOpen}
          maxWidth="lg"
          onClose={() => setModalSubmitConfirmationOpen(false)}
        >
          <DialogTitle>Submit Changes</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to submit all changes? <br />
              This action will apply all modifications made to the feature
              flags.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setModalSubmitConfirmationOpen(false)}
              color="primary"
            >
              Cancel
            </Button>
            <Button onClick={() => handleSubmitChanges()} color="secondary">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={openOperationAlert}
        onClose={() => setOpenOperationAlert(false)}
        autoHideDuration={5000}
      >
        <Alert
          onClose={() => setOpenOperationAlert(false)}
          severity={operationSeverity}
        >
          {operationMsg}
        </Alert>
      </Snackbar>
    </>
  );
}